import { TTheme } from "@highway/mbs-shared";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

interface IThemeMode {
    currentMode: Exclude<TTheme, "system">;
    handleTheme: (theme: TTheme) => void;
    handleDefaultTheme: () => void;
}

const ThemeContext = createContext<IThemeMode>({
    currentMode: "light",
    handleTheme: () => {},
    handleDefaultTheme: () => {},
});

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
    const [rawMode, setRawMode] = useState<TTheme>("system");

    const [systemPreference, setSystemPreference] = useState<"light" | "dark">(() =>
        typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light",
    );

    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e: MediaQueryListEvent) => setSystemPreference(e.matches ? "dark" : "light");
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    const currentMode: Exclude<TTheme, "system"> = rawMode === "system" ? systemPreference : rawMode;

    const handleDefaultTheme = useCallback(() => {
        const localTheme = localStorage.getItem("mbs-theme");
        if (localTheme) setRawMode(localTheme as TTheme);
    }, []);

    const handleTheme = useCallback((theme: TTheme) => {
        localStorage.setItem("mbs-theme", theme);
        setRawMode(theme);
    }, []);

    return (
        <ThemeContext.Provider value={{ currentMode, handleTheme, handleDefaultTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useThemeMode = () => {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error("useThemeMode must be used within a ThemeModeProvider");
    }

    return context;
};
